/**
 * The raw packet. This is the screen's reason to exist: MedStar's acceptance
 * criterion is seeing the HL7 that arrived, not a form populated from it.
 *
 * Renders `segments`, never `raw.split('\r')`. The harness pre-splits precisely
 * so a browser does not have to, and a message that has been through a text
 * editor can arrive with \r\n or \n, which a naive split gets wrong.
 *
 * A 404 here is normal, not an error: the store is a bounded ring, so an older
 * packet is simply gone.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, Copy, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { dataLayerApi } from '@/api/dataLayerApi'
import { DataLayerError } from '@/lib/dataLayer/errors'
import { messageTypeLabel, packetTimestamp } from '@/lib/dataLayer/messageView'
import type { Hl7Message } from '@/lib/dataLayer/types'

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-muted-foreground opacity-60 hover:opacity-100"
      title="Copy"
      onClick={async () => {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  )
}

function Er7Block({ segments, label }: { segments: string[]; label: string }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <CopyButton value={segments.join('\r')} />
      </div>
      <pre className="max-h-64 overflow-auto rounded-lg border border-border/60 bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        {segments.map((segment, i) => (
          <div key={i} className="whitespace-pre-wrap break-all">
            {segment}
          </div>
        ))}
      </pre>
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate font-mono text-xs">{value}</p>
    </div>
  )
}

export function MessageDetailDialog({
  messageId,
  onClose,
}: {
  messageId: string | null
  onClose: () => void
}) {
  const { data, error, isLoading } = useQuery<Hl7Message>({
    queryKey: ['data-layer', 'message', messageId],
    queryFn: () => dataLayerApi.getMessage(messageId as string),
    enabled: !!messageId,
    retry: false,
    staleTime: Infinity,
  })

  const failure = error instanceof DataLayerError ? error.failure : null

  return (
    <Dialog open={!!messageId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Raw packet
            {data && <Badge variant="secondary">{messageTypeLabel(data)}</Badge>}
            {data?.mimic && <Badge variant="secondary">simulated</Badge>}
          </DialogTitle>
          <DialogDescription>
            {data
              ? `${data.direction === 'in' ? 'Received' : 'Sent'} on ${data.channelId} at ${packetTimestamp(data.receivedAt)} · ${data.peer}`
              : 'Loading the message body.'}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {failure && (
          <div className="rounded-lg border border-dashed py-10 text-center">
            <p className="text-sm font-medium">
              {failure.kind === 'evicted' ? 'Packet no longer available' : 'Could not load the packet'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{failure.message}</p>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* The reason this screen exists, per the reference UI map: showing
                a bad message and why we rejected it. */}
            {data.parseError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div>
                  <p className="text-sm font-medium text-destructive">We could not parse this message</p>
                  <p className="text-sm text-muted-foreground">{data.parseError}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Ack" value={data.ackCode ?? 'no ACK'} />
              <Field label="Bytes" value={data.bytes} />
              <Field label="Latency" value={`${data.latencyMs}ms`} />
              <Field label="Seq" value={data.seq} />
              {data.msh && <Field label="Control id" value={data.msh.controlId} />}
              {data.msh && <Field label="Version" value={data.msh.version} />}
              {data.localPort !== null && <Field label="Local port" value={data.localPort} />}
              {data.attempts !== undefined && <Field label="Attempts" value={data.attempts} />}
            </div>

            {data.msh && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-border/60 bg-muted/20 p-3 sm:grid-cols-4">
                <Field label="Sending app" value={data.msh.sendingApp} />
                <Field label="Sending facility" value={data.msh.sendingFacility} />
                <Field label="Receiving app" value={data.msh.receivingApp} />
                <Field label="Receiving facility" value={data.msh.receivingFacility} />
              </div>
            )}

            {/* Outbound: a peer that echoes a different control id has not
                acknowledged our message, whatever code it returned. */}
            {data.direction === 'out' && data.acknowledgedControlId !== undefined && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Control id sent" value={data.msh?.controlId ?? '—'} />
                <Field label="Control id echoed" value={data.acknowledgedControlId ?? '—'} />
              </div>
            )}

            {data.reason && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {data.reason}
              </p>
            )}

            <Er7Block segments={data.segments} label="Message" />

            {data.ack ? (
              <Er7Block
                segments={data.ack.split(/\r\n|\r|\n/).filter(Boolean)}
                label={data.direction === 'in' ? 'Acknowledgment we returned' : 'Acknowledgment the peer returned'}
              />
            ) : (
              <p className="text-xs text-muted-foreground">No acknowledgment was recorded.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
